import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { authAPI } from '../../services/api';
import toast from '../../services/toast';
import { AuthSplitShell } from '../../components/auth/AuthSplitShell';
import Input from '../../components/ui/Input';
import {
  authLabelClass,
  authPrimaryButton,
  authFooterClass,
  authFooterLink,
  authPasswordToggle,
} from '../../components/auth/authViewTheme';

const INVALID_LINK_MESSAGE =
  'Invalid or expired reset link. Request a new one from the login page.';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({ defaultValues: { newPassword: '', confirmPassword: '' } });

  if (!token) {
    return (
      <AuthSplitShell title="Reset link invalid" description={INVALID_LINK_MESSAGE}>
        <p className={authFooterClass}>
          <Link to="/forgot-password" className={authFooterLink}>
            Request a new link
          </Link>
          {' · '}
          <Link to="/" className={authFooterLink}>
            Back to sign in
          </Link>
        </p>
      </AuthSplitShell>
    );
  }

  const onSubmit = handleSubmit(async ({ newPassword, confirmPassword }) => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      await authAPI.resetPassword(token, newPassword, confirmPassword);
      toast.success('Password has been reset. You can now sign in.');
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(
        (err as Error).message && (err as Error).message.includes('Invalid or expired')
          ? INVALID_LINK_MESSAGE
          : (err as Error).message || 'Failed to reset password.'
      );
    }
  });

  return (
    <AuthSplitShell title="Set new password" description="Enter your new password below">
      <form className="space-y-5" onSubmit={onSubmit} noValidate>
        <div>
          <label htmlFor="reset-new-password" className={authLabelClass}>
            New password
          </label>
          <div className="relative">
            <Input
              id="reset-new-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className="pr-11"
              placeholder="At least 6 characters"
              {...register('newPassword', { required: true, minLength: 6 })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              className={authPasswordToggle}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" aria-hidden />
              ) : (
                <Eye className="h-4 w-4" aria-hidden />
              )}
            </button>
          </div>
        </div>
        <div>
          <label htmlFor="reset-confirm-password" className={authLabelClass}>
            Confirm password
          </label>
          <Input
            id="reset-confirm-password"
            type="password"
            autoComplete="new-password"
            placeholder="Confirm your password"
            {...register('confirmPassword', { required: true })}
          />
        </div>
        <button type="submit" disabled={isSubmitting} className={authPrimaryButton}>
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Resetting…
            </>
          ) : (
            <>
              <Lock className="h-4 w-4 shrink-0" aria-hidden />
              Reset password
            </>
          )}
        </button>
      </form>
      <p className={authFooterClass}>
        <Link to="/" className={authFooterLink}>
          Back to sign in
        </Link>
      </p>
    </AuthSplitShell>
  );
};

export default ResetPassword;
